import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Create styled components for better responsiveness
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%), rgba(255, 255, 255, 0.95);
  padding: 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const MainTitle = styled.h1`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const SubTitle = styled.p`
  color: #4a5568;
  font-size: 1rem;
  font-weight: 300;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const TabContainer = styled.div`
  background-color: white;
  padding: 0.75rem;
  border-radius: 20px;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.08);
  border: 1px solid rgba(102, 126, 234, 0.1);
  
  @media (min-width: 768px) {
    padding: 1rem;
    margin-bottom: 2rem;
  }
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border: ${props => props.active ? 'none' : '2px solid rgba(102, 126, 234, 0.2)'};
  padding: 0.6rem 1rem;
  border-radius: 15px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
  justify-content: center;
  
  @media (min-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    min-width: 120px;
  }
  
  &:hover {
    border-color: ${props => props.active ? 'none' : '#667eea'};
    background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(102, 126, 234, 0.05)'};
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  
  @media (min-width: 576px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
  
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
  }
`;

const SelectPlanPage = () => {
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plansRef = collection(db, 'plans');
        const plansQuery = query(plansRef, orderBy("preference", "asc"));
        const plansSnap = await getDocs(plansQuery);
        const planList = [];

        plansSnap.forEach(doc => {
          const data = doc.data();
          planList.push({ id: doc.id, ...data });
        });

        setPlans(planList);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };

    fetchPlans();
  }, []);

  const getFilteredPlans = () => {
    let filtered = [];
    
    if (activeTab === 'all') {
      // Show bundle plans first, then others
      const bundlePlans = plans.filter(plan => 
        plan.category?.toLowerCase() === 'bundle'
      );
      const otherPlans = plans.filter(plan => 
        plan.category?.toLowerCase() !== 'bundle'
      );
      filtered = [...bundlePlans, ...otherPlans];
    } else {
      filtered = plans.filter(plan => 
        plan.category?.toLowerCase() === activeTab.toLowerCase()
      );
    }
    
    return filtered;
  };

  const tabs = [
    { id: 'all', name: 'All Plans', icon: '🌟' },
    { id: 'diet', name: 'Diet', icon: '🥗' },
    { id: 'workout', name: 'Workout', icon: '💪' }
  ];

  const filteredPlans = getFilteredPlans();

  const PlanCard = ({ plan, isFeatured }) => (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '25px',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: isFeatured 
          ? '0 20px 40px rgba(102, 126, 234, 0.25)' 
          : '0 15px 35px rgba(0,0,0,0.08)',
        transition: 'all 0.4s ease',
        position: 'relative',
        border: isFeatured 
          ? '3px solid transparent'
          : '1px solid rgba(102, 126, 234, 0.1)',
        background: isFeatured
          ? 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea, #764ba2) border-box'
          : 'white'
      }}
      onClick={() => navigate(`/customer/select-plan/${plan.id}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-10px)';
        e.currentTarget.style.boxShadow = isFeatured
          ? '0 30px 60px rgba(102, 126, 234, 0.35)'
          : '0 25px 50px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = isFeatured
          ? '0 20px 40px rgba(102, 126, 234, 0.25)'
          : '0 15px 35px rgba(0,0,0,0.08)';
      }}
    >
      {/* Featured Badge */}
      {isFeatured && (
        <div style={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '0.5rem 1.5rem',
          borderRadius: '20px',
          fontSize: '0.8rem',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          zIndex: 1,
          boxShadow: '0 5px 15px rgba(102, 126, 234, 0.4)'
        }}>
          ⭐ FEATURED
        </div>
      )}

      {/* Image Section */}
      <div style={{
        height: '180px',
        background: plan.imageUrl 
          ? `url(${plan.imageUrl})` 
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: '15px',
          right: '15px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '0.5rem 1rem',
          borderRadius: '20px',
          fontSize: '0.9rem',
          fontWeight: '600',
          color: '#667eea',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          backdropFilter: 'blur(10px)'
        }}>
          {plan.category}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1.5rem' }}>
        <h3 style={{
          fontSize: '1.3rem',
          fontWeight: '700',
          color: '#2d3748',
          marginBottom: '0.5rem',
          lineHeight: '1.3'
        }}>
          {plan.name}
        </h3>
        
        <p style={{
          color: '#718096',
          fontSize: '0.95rem',
          marginBottom: '1rem',
          lineHeight: '1.5'
        }}>
          {plan.description && plan.description.length > 100 
            ? `${plan.description.substring(0, 100)}...` 
            : plan.description}
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'auto'
        }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: '800',
            color: '#4a5568'
          }}>
            ${plan.price}
          </div>
          
          <div style={{
            fontSize: '0.9rem',
            color: '#718096'
          }}>
            {plan.duration} Days
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <PageContainer>
      <ContentContainer>
        <HeaderSection>
          <MainTitle>
            Choose Your Perfect Plan
          </MainTitle>
          <SubTitle>
            Transform your lifestyle with our premium plans
          </SubTitle>
        </HeaderSection>

        {/* Tab Navigation */}
        <TabContainer>
          <TabsWrapper>
            {tabs.map(tab => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                {tab.name}
              </TabButton>
            ))}
          </TabsWrapper>
        </TabContainer>

        {/* Plans Grid */}
        <PlansGrid>
          {filteredPlans.map((plan, index) => {
            const isBundle = plan.category?.toLowerCase() === 'bundle';
            const isFeatured = activeTab === 'all' && isBundle;
            
            return (
              <PlanCard 
                key={plan.id} 
                plan={plan} 
                isFeatured={isFeatured} 
              />
            );
          })}
        </PlansGrid>
      </ContentContainer>
    </PageContainer>
  );
};

export default SelectPlanPage;